import {
  provideZoneChangeDetection,
  importProvidersFrom,
  provideAppInitializer,
  inject,
  ApplicationConfig,
  LOCALE_ID,
  DEFAULT_CURRENCY_CODE
} from '@angular/core';
import { provideHttpClient, HttpClient, withInterceptors } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoaderInterceptor } from 'src/app/interceptors/loader.interceptor';
import { ErrorInterceptor } from 'src/app/interceptors/error.interceptor';
import { HealthService } from 'src/app/services/health.service';
import { GlobalErrorService } from 'src/app/services/global-error.service';
import { LogService } from 'src/app/services/log.service';
import { logger } from 'src/app/utils';
import { environment } from 'src/environments/environment';
import { IntlService } from '@progress/kendo-angular-intl';
import { CalendarIntlService } from 'src/app/services/calendar-intl.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DEFAULT_LANGUAGE } from 'src/app/app.constants';
import { provideTranslateService } from '@ngx-translate/core';
import { provideRouter, withHashLocation } from '@angular/router';
import { routes } from './app.routes';

import '@angular/common/locales/global/en';
import '@angular/common/locales/global/de';
import '@angular/common/locales/global/it';

import '@progress/kendo-angular-intl/locales/en/all';
import '@progress/kendo-angular-intl/locales/de/all';
import '@progress/kendo-angular-intl/locales/it/all';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: IntlService,
      useClass: CalendarIntlService,
    },
    {
      provide: LOCALE_ID,
      useValue: DEFAULT_LANGUAGE
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR'
    },
    provideRouter(routes, withHashLocation()),
    provideZoneChangeDetection({eventCoalescing: true}),
    provideHttpClient(withInterceptors([LoaderInterceptor, ErrorInterceptor])),
    // Ping the public health endpoint once on startup. Fire-and-forget so it never
    // blocks app start; if the backend is unhealthy we surface the error dialog proactively.
    provideAppInitializer(() => {
      const healthService = inject(HealthService);
      const globalErrorService = inject(GlobalErrorService);
      const logService = inject(LogService);
      healthService.check().subscribe(ok => {
        logger.i('Backend health on startup', ok);
        if (!ok) {
          const context = { source: 'startup-health', requestUrl: environment.healthUrl };
          logService.logError(context);
          // isServerError=true: the health endpoint failed, so the backend is down.
          globalErrorService.open(logService.buildReport(context), true);
        }
      });
    }),
    provideTranslateService({defaultLanguage: DEFAULT_LANGUAGE}),
    importProvidersFrom(
      BrowserAnimationsModule,
      TranslateModule.forRoot({
        loader: {
          deps: [HttpClient],
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
        }
      })
    )
  ]
};
